import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";

const LINE_EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: LINE_EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  ["60s", "To get started"],
  ["Zero", "Obligation"],
  ["Any", "Condition"],
  ["100%", "Local Columbus"],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      className="relative bg-evergreen text-cream overflow-hidden"
      data-testid="hero"
    >
      <div className="absolute top-0 right-0 w-[45%] h-[45%] bg-gold/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="max-w-[1180px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] uppercase text-gold-light mb-8 border border-gold/30 rounded-full px-4 py-1.5"
          >
            <MapPin size={12} /> Columbus, Georgia
          </motion.p>

          <h1 className="font-serif font-light text-cream leading-[0.95] tracking-tight text-[clamp(44px,7vw,84px)]">
            <Line delay={0.35}>Thinking about</Line>
            <Line delay={0.47}>selling your</Line>
            <Line delay={0.59}>
              <span className="italic text-gold-light">Columbus</span> home?
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-8 text-[17px] md:text-lg text-cream/70 max-w-[480px] leading-relaxed"
          >
            Explore your selling options without committing to a traditional listing. No obligation, no pressure — just clarity from a local team.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#start"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white font-mono text-[12px] tracking-[0.15em] uppercase px-8 py-4 hover:bg-gold-hover transition-colors duration-300"
            >
              Explore My Options
              <ArrowDown size={15} className="group-hover:translate-y-1 transition-transform duration-300" />
            </a>
            <a
              href="#options"
              className="inline-flex items-center gap-2 font-mono text-[12px] tracking-[0.15em] uppercase px-6 py-4 border border-cream/25 text-cream hover:border-gold-light hover:text-gold-light transition-colors duration-300"
            >
              How It Works
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.55, duration: 1.3, ease: LINE_EASE }}
          className="relative h-[380px] sm:h-[480px] lg:h-[560px]"
        >
          <div className="absolute inset-0 clip-frame overflow-hidden shadow-[0_40px_120px_rgba(0,0,0,0.45)] ring-1 ring-gold/20">
            <motion.img
              src="https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Traditional brick home in Columbus, Georgia"
              style={{ y: imgY, scale: imgScale }}
              className="w-full h-[120%] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-5 bg-cream text-ink px-6 py-4 shadow-xl">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-soft">No listing required</p>
            <p className="font-serif text-2xl text-gold mt-1">Any condition welcome</p>
          </div>
        </motion.div>
      </div>

      {/* stats bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="border-t border-cream/10"
        data-testid="hero-stats"
      >
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-cream/10">
          {STATS.map(([big, small]) => (
            <div key={small} className="py-7 px-6 first:pl-0">
              <p className="font-serif text-4xl text-gold-light">{big}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-cream/60 mt-1">{small}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
